import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { OPS_INCIDENT, OPS_INCIDENTS } from '../queries/opsIncidents';
import { CREATE_OPS_INCIDENT, RUN_LOG_MONITOR, UPDATE_OPS_INCIDENT } from '../mutations/opsIncidents';

const NONE = [];

/**
 * The ops incident tickets, filtered. `status: ''` means every status — sent
 * as such, because the API's own default for a missing status is "open".
 * Empty severity, priority and service are sent as null: no filter.
 */
export function useOpsIncidents({ status = 'open', severity = '', priority = '', service = '', limit = 200 } = {}) {
  const { data, loading, error, refetch } = useQuery(OPS_INCIDENTS, {
    variables: {
      status,
      severity: severity || null,
      priority: priority || null,
      service: service || null,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.opsIncidents ?? NONE,
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/** One ticket, whole; skipped with no id. */
export function useOpsIncident(id) {
  const { data, loading, error, refetch } = useQuery(OPS_INCIDENT, {
    variables: { id: Number(id) },
    skip: id == null,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return { incident: data?.opsIncident ?? null, loading, error, refetch };
}

const REFRESH = { refetchQueries: ['OpsIncidents', 'OpsIncident'] };
export const useCreateOpsIncident = () => useMutation(CREATE_OPS_INCIDENT, REFRESH);
export const useUpdateOpsIncident = () => useMutation(UPDATE_OPS_INCIDENT, REFRESH);
/* A real run raises and updates tickets, so the list is read again after it. */
export const useRunLogMonitor = () => useMutation(RUN_LOG_MONITOR, { refetchQueries: ['OpsIncidents'] });
